import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  forwardRef,
} from "react"
import { Input, Spinner } from "@heroui/react"
import clsx from "clsx"
import { CheckCircleIcon, XCircleIcon } from "@heroicons/react/24/solid"
import { XMarkIcon } from "@heroicons/react/24/outline"

const MAX_CONCURRENT_VERIFICATIONS = 10

const STATUS_PENDING = "pending"
const STATUS_CHECKING = "checking"
const STATUS_VALID = "valid"
const STATUS_INVALID = "invalid"

const VERIFICATION_ERROR_MESSAGE = "Unable to verify this URL. Check the address and try again."

const normalizeListProp = (value) => {
  if (!value) return []
  if (Array.isArray(value)) {
    return value
  }
  if (typeof value === "string") {
    return value
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean)
  }
  return []
}

const ensureProtocol = (input) => {
  if (!input) return null
  let value = input.trim()
  if (!value) return null
  if (!/^https?:\/\//i.test(value)) {
    value = `https://${value}`
  }
  try {
    const url = new URL(value)
    url.hash = ""
    return url.toString().replace(/\/$/, "")
  } catch (error) {
    return null
  }
}

const extractHostname = (input) => {
  if (!input) return null
  try {
    const url = new URL(ensureProtocol(input) ?? input)
    return url.hostname.replace(/^www\./i, "")
  } catch (error) {
    return null
  }
}

const makeId = (() => {
  let counter = 0
  return () => {
    counter += 1
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
      return crypto.randomUUID()
    }
    return `verified-url-${Date.now()}-${counter}`
  }
})()

const normalizeCandidate = (candidate) => {
  if (!candidate) return undefined
  const domain =
    candidate.domain ??
    extractHostname(candidate.url ?? candidate.website_url ?? candidate.website) ??
    null
  return {
    name: candidate.name ?? candidate.company ?? candidate.title ?? "",
    domain,
    url:
      ensureProtocol(candidate.url ?? candidate.website_url ?? candidate.website ?? candidate.domain) ??
      null,
    description: candidate.description ?? candidate.summary ?? undefined,
  }
}

const selectCompanyMatch = (candidates, hostname) => {
  if (!Array.isArray(candidates) || candidates.length === 0) {
    return undefined
  }
  const normalized = candidates
    .map(normalizeCandidate)
    .filter((candidate) => candidate?.domain)
  if (!normalized.length) {
    return undefined
  }
  const domain = hostname?.replace(/^www\./i, "")
  if (domain) {
    const exact = normalized.find((candidate) => candidate.domain?.replace(/^www\./i, "") === domain)
    if (exact) return exact
  }
  return normalized[0]
}

const fallbackUrlCheck = async (url) => {
  if (!url) return false
  try {
    const previewResponse = await fetch(`/api/preview?url=${encodeURIComponent(url)}`)
    if (previewResponse.ok) {
      return true
    }
  } catch (error) {
    // Ignore preview errors and fall back to structural validation.
  }

  try {
    const parsed = new URL(url)
    return Boolean(parsed.protocol && parsed.hostname)
  } catch (error) {
    return false
  }
}

const verifyUrlData = async (url, hostname) => {
  let company
  let verified = false

  try {
    const response = await fetch(`/api/companyDetails?name=${encodeURIComponent(hostname)}`)
    if (response.ok) {
      const data = await response.json()
      company = selectCompanyMatch(data, hostname)
      verified = Boolean(company)
    }
  } catch (error) {
    // Ignore network errors and fall back to structural validation.
  }

  if (!verified) {
    verified = await fallbackUrlCheck(url)
  }

  return { verified, company }
}

const buildEntry = (rawValue) => {
  const normalizedUrl = ensureProtocol(rawValue)
  const hostname = extractHostname(normalizedUrl ?? rawValue)
  if (!normalizedUrl || !hostname) {
    return null
  }
  return {
    id: makeId(),
    raw: rawValue.trim(),
    url: normalizedUrl,
    hostname,
    status: STATUS_PENDING,
    company: undefined,
    error: null,
  }
}

const VerifiedUrl = forwardRef(
  (
    {
      value,
      defaultValue,
      onChange,
      onValidationChange,
      maxUrls = Infinity,
      label,
      description,
      placeholder = "e.g. example.com",
      disabled,
      className,
      inputClassName,
      autoFocus,
      embedded = false,
    },
    ref,
  ) => {
    const lastUrlsRef = useRef("")
    const [items, setItems] = useState(() => {
      const maxInitial = Number.isFinite(maxUrls) ? Math.max(0, maxUrls) : maxUrls
      const initialValues = normalizeListProp(value ?? defaultValue).slice(0, maxInitial)
      const entries = initialValues
        .map(buildEntry)
        .filter(Boolean)
      lastUrlsRef.current = entries.map((entry) => entry.url).join("|")
      return entries
    })
    const [inputValue, setInputValue] = useState("")

    const inflightIdsRef = useRef(new Set())
    const mountedRef = useRef(false)
    useEffect(() => {
      mountedRef.current = true
      return () => {
        mountedRef.current = false
      }
    }, [])

    const applyItemsUpdate = useCallback(
      (updater, options = {}) => {
        const { notify = true } = options
        setItems((prev) => {
          const next = typeof updater === "function" ? updater(prev) : updater
          if (next === prev) {
            return prev
          }
          const urls = next.map((item) => item.url)
          const serialized = urls.join("|")
          if (notify) {
            if (serialized !== lastUrlsRef.current) {
              lastUrlsRef.current = serialized
              onChange?.(urls)
            } else {
              lastUrlsRef.current = serialized
            }
          } else {
            lastUrlsRef.current = serialized
          }
          return next
        })
      },
      [onChange],
    )

    const updateEntry = useCallback(
      (entryId, mutator, options) => {
        applyItemsUpdate((prev) => {
          let changed = false
          const next = prev.map((item) => {
            if (item.id !== entryId) {
              return item
            }
            const updated = mutator(item)
            if (!updated || updated === item) {
              return item
            }
            changed = true
            return updated
          })
          return changed ? next : prev
        }, options)
      },
      [applyItemsUpdate],
    )

    const startVerification = useCallback(
      async (entry) => {
        const { id, url, hostname } = entry
        if (!url || !hostname) {
          updateEntry(
            id,
            (item) => ({
              ...item,
              status: STATUS_INVALID,
              error: VERIFICATION_ERROR_MESSAGE,
            }),
            { notify: false },
          )
          return
        }

        inflightIdsRef.current.add(id)
        updateEntry(
          id,
          (item) => ({
            ...item,
            status: STATUS_CHECKING,
            error: null,
          }),
          { notify: false },
        )

        let result
        try {
          result = await verifyUrlData(url, hostname)
        } catch (error) {
          result = { verified: false, company: undefined }
        }

        inflightIdsRef.current.delete(id)
        if (!mountedRef.current) {
          return
        }

        const { verified, company } = result
        updateEntry(
          id,
          (item) => {
            if (item.url !== url) {
              return item
            }
            if (verified) {
              return {
                ...item,
                status: STATUS_VALID,
                error: null,
                company: company ?? item.company ?? undefined,
              }
            }
            return {
              ...item,
              status: STATUS_INVALID,
              error: VERIFICATION_ERROR_MESSAGE,
            }
          },
          { notify: false },
        )
      },
      [updateEntry],
    )

    useEffect(() => {
      if (!mountedRef.current) {
        return
      }
      const availableSlots = Math.max(
        0,
        MAX_CONCURRENT_VERIFICATIONS - inflightIdsRef.current.size,
      )
      if (availableSlots <= 0) {
        return
      }

      const queued = items.filter(
        (item) => item.status === STATUS_PENDING && !inflightIdsRef.current.has(item.id),
      )
      if (!queued.length) {
        return
      }

      queued.slice(0, availableSlots).forEach((entry) => {
        startVerification(entry)
      })
    }, [items, startVerification])

    const controlledValuesKey = useMemo(() => {
      if (!value) return null
      try {
        const normalized = normalizeListProp(value)
        const limited = Number.isFinite(maxUrls)
          ? normalized.slice(0, Math.max(0, maxUrls))
          : normalized
        return JSON.stringify([...limited].sort())
      } catch (error) {
        return null
      }
    }, [value, maxUrls])

    useEffect(() => {
      if (controlledValuesKey === null) {
        return
      }

      const incomingValues = normalizeListProp(value).slice(0, maxUrls)
      const incomingUrls = new Set()

      applyItemsUpdate(
        (prev) => {
          const previousByUrl = new Map(prev.map((item) => [item.url, item]))
          const next = []

          incomingValues.forEach((rawValue) => {
            const entry = buildEntry(rawValue)
            if (!entry || incomingUrls.has(entry.url)) {
              return
            }
            incomingUrls.add(entry.url)
            const existing = previousByUrl.get(entry.url)
            if (existing) {
              const trimmed = rawValue.trim()
              if (trimmed !== existing.raw) {
                next.push({ ...existing, raw: trimmed })
              } else {
                next.push(existing)
              }
            } else {
              if (next.length < maxUrls) {
                next.push(entry)
              }
            }
          })

          const removedIds = []
          prev.forEach((item) => {
            if (!incomingUrls.has(item.url)) {
              removedIds.push(item.id)
            }
          })

          if (removedIds.length) {
            removedIds.forEach((id) => inflightIdsRef.current.delete(id))
          }

          const orderUnchanged =
            next.length === prev.length && next.every((item, index) => item === prev[index])

          if (orderUnchanged && removedIds.length === 0) {
            return prev
          }

          return next
        },
        { notify: false },
      )
    }, [applyItemsUpdate, controlledValuesKey, value, maxUrls])

    useEffect(() => {
      if (!onValidationChange) {
        return
      }
      const pendingCount = items.filter(
        (item) => item.status === STATUS_PENDING || item.status === STATUS_CHECKING,
      ).length
      const payload = {
        total: items.length,
        valid: items.filter((item) => item.status === STATUS_VALID).length,
        pending: pendingCount,
        invalid: items.filter((item) => item.status === STATUS_INVALID).length,
        items: items.map(({ url, status, company, error }) => ({
          url,
          status,
          company,
          error,
        })),
      }
      onValidationChange(payload)
    }, [items, onValidationChange])

    const addEntries = useCallback(
      (rawValue) => {
        if (!rawValue) return
        const candidates = rawValue
          .split(",")
          .map((segment) => segment.trim())
          .filter(Boolean)
        if (!candidates.length) return

        applyItemsUpdate((prev) => {
          const remainingSlots = Math.max(0, maxUrls - prev.length)
          if (remainingSlots <= 0) {
            return prev
          }
          const existing = new Set(prev.map((item) => item.url))
          const next = [...prev]
          let changed = false
          let slotsLeft = remainingSlots

          candidates.forEach((candidate) => {
            const entry = buildEntry(candidate)
            if (!entry || existing.has(entry.url) || slotsLeft <= 0) {
              return
            }
            existing.add(entry.url)
            next.push(entry)
            changed = true
            slotsLeft -= 1
          })

          return changed ? next : prev
        })
      },
      [applyItemsUpdate, maxUrls],
    )

    const removeEntry = useCallback(
      (entryId) => {
        let removed = false
        applyItemsUpdate((prev) => {
          const index = prev.findIndex((item) => item.id === entryId)
          if (index === -1) {
            return prev
          }
          const next = [...prev]
          next.splice(index, 1)
          removed = true
          return next
        })
        if (removed) {
          inflightIdsRef.current.delete(entryId)
        }
      },
      [applyItemsUpdate],
    )

    const removeLastEntry = useCallback(() => {
      let removedId
      applyItemsUpdate((prev) => {
        if (prev.length === 0) {
          return prev
        }
        const next = prev.slice(0, -1)
        removedId = prev[prev.length - 1]?.id
        return next
      })
      if (removedId) {
        inflightIdsRef.current.delete(removedId)
      }
    }, [applyItemsUpdate])

    const handleKeyDown = useCallback(
      (event) => {
        if (["Enter", "Tab", ","].includes(event.key)) {
          event.preventDefault()
          if (inputValue.trim()) {
            addEntries(inputValue)
            setInputValue("")
          }
        }
      },
      [addEntries, inputValue, removeLastEntry],
    )

    const handleBlur = useCallback(() => {
      if (inputValue.trim()) {
        addEntries(inputValue)
        setInputValue("")
      }
    }, [addEntries, inputValue])

    useImperativeHandle(
      ref,
      () => ({
        getUrls: () => items.map((item) => item.url),
        getStatuses: () =>
          items.map(({ url, status, company, error }) => ({
            url,
            status,
            company,
            error,
          })),
        isAnyPending: () =>
          items.some((item) => item.status === STATUS_PENDING || item.status === STATUS_CHECKING),
        isAllValid: () =>
          items.length > 0 && items.every((item) => item.status === STATUS_VALID),
        clear: () => {
          inflightIdsRef.current.clear()
          applyItemsUpdate([], { notify: true })
        },
      }),
      [items, applyItemsUpdate],
    )

    const showLabel = !embedded && (label || description)

    const inputRowClasses = embedded
      ? "flex flex-wrap items-center gap-2 px-2"
      : "flex flex-wrap items-center gap-2 px-4 py-3"

    const listWrapperClasses = embedded
      ? "divide-y divide-slate-200/80"
      : "divide-y divide-slate-200/80"

    const listItemClasses = embedded ? "flex items-start gap-3 py-2" : "flex items-start gap-3 px-4 py-3"

    const inputDisabled = disabled || items.length >= maxUrls
    const shouldHideInput =
      inputDisabled && Number.isFinite(maxUrls) && maxUrls <= 1 && items.length >= maxUrls

    return (
      <div className={clsx("flex flex-col gap-2", className)}>
        {showLabel && (
          <div className="flex flex-col gap-1">
            {label && <span className="text-sm font-medium text-slate-800">{label}</span>}
            {description && <span className="text-xs text-slate-500">{description}</span>}
          </div>
        )}
        <div
          className={clsx(
            embedded
              ? "rounded-xl border border-slate-200/80 bg-white/90"
              : "rounded-2xl border border-slate-200 bg-white shadow-sm",
          )}
        >
          {!shouldHideInput && (
            <div className={inputRowClasses}>
              <Input
                size="lg"
                variant="bordered"
                radius="lg"
                labelPlacement="outside"
                isDisabled={inputDisabled}
                autoFocus={autoFocus}
                value={inputValue}
                onValueChange={setInputValue}
                onKeyDown={handleKeyDown}
                onBlur={handleBlur}
                className={clsx("min-w-[240px] flex-1", inputClassName)}
                classNames={{
                  inputWrapper: "shadow-none border-0 bg-transparent px-0",
                  input: "text-sm",
                }}
                placeholder={placeholder}
              />
            </div>
          )}
          {items.length > 0 && (
            <ul className={listWrapperClasses}>
              {items.map((item) => {
                const isPending = item.status === STATUS_PENDING || item.status === STATUS_CHECKING
                const isValid = item.status === STATUS_VALID
                const isInvalid = item.status === STATUS_INVALID

                const company = item.company ?? {}
                const companyName = company.name || item.hostname || item.url
                const companyDomain = company.domain ?? item.hostname
                const companyUrl = company.url ?? item.url

                const categories = Array.isArray(company.categories)
                  ? company.categories
                  : typeof company.categories === "string"
                  ? company.categories.split(/[,|]/).map((value) => value.trim())
                  : []
                const topCategory = categories[0]

                const metaParts = [
                  topCategory,
                  company.founded ?? company.founded_year ?? company.founded_at,
                  company.location,
                ].filter(Boolean)

                const detailLine =
                  metaParts.length > 0 ? metaParts.join(" • ") : company.description

                const logoUrl = (() => {
                  if (!isValid) return null
                  if (company.logo) {
                    return `/api/remoteImage?url=${encodeURIComponent(company.logo)}`
                  }
                  if (companyDomain) {
                    return `/api/companyLogo?domain=${encodeURIComponent(companyDomain)}`
                  }
                  if (companyName) {
                    return `/api/companyLogo?name=${encodeURIComponent(companyName)}`
                  }
                  return null
                })()

                const statusBadge = (() => {
                  if (isPending) {
                    return (
                      <div className="flex items-center gap-2 text-xs font-medium text-slate-500">
                        <Spinner size="sm" />
                        <span>Verifying…</span>
                      </div>
                    )
                  }
                  if (isInvalid) {
                    return (
                      <div className="flex items-center gap-2 text-xs font-medium text-rose-500">
                        <XCircleIcon className="h-5 w-5" />
                        <span>Verification failed</span>
                      </div>
                    )
                  }
                  return (
                    <div className="flex flex-col items-end gap-1 text-right">
                      <span className="flex items-center gap-1 text-xs font-semibold text-emerald-600">
                        <CheckCircleIcon className="h-4 w-4" />
                        {companyName ? `Found: ${companyName}` : "URL verified"}
                      </span>
                      {detailLine && (
                        <span className="max-w-[220px] text-[11px] text-slate-500 line-clamp-2">
                          {detailLine}
                        </span>
                      )}
                    </div>
                  )
                })()

                return (
                  <li key={item.id} className={clsx(listItemClasses, "items-center gap-4 px-2")}>
                    <div className="flex min-w-0 flex-1 flex-col gap-1">
                      <span className="truncate text-sm font-medium text-slate-900">
                        {item.hostname ?? item.url}
                      </span>
                      {companyUrl ? (
                        <a
                          className="text-xs font-medium text-slate-500 hover:text-slate-700"
                          href={companyUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {companyDomain}
                        </a>
                      ) : (
                        <span className="text-xs font-medium text-slate-500">{companyDomain}</span>
                      )}
                      {isInvalid && item.error && (
                        <p className="text-xs text-rose-500">{item.error}</p>
                      )}
                    </div>
                    <div className="flex shrink-0 items-center gap-3">
                      {statusBadge}
                      {logoUrl && isValid && (
                        <img
                          src={logoUrl}
                          alt={`${companyName} logo`}
                          className="h-8 w-8 rounded-full border border-slate-200 bg-white object-contain p-1"
                          loading="lazy"
                        />
                      )}
                      <button
                        type="button"
                        className="rounded-full p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
                        onClick={() => removeEntry(item.id)}
                        aria-label={`Remove ${item.url}`}
                        disabled={disabled}
                      >
                        <XMarkIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </li>
                )
              })}
            </ul>
          )}
        </div>
      </div>
    )
  },
)

VerifiedUrl.displayName = "VerifiedUrl"

export default VerifiedUrl
